
import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { initializeSecurity, validateUserAuthentication } from '@/utils/securityUtils';
import { useProviderAuth } from '@/hooks/useProviderAuth';

interface SecurityContextType {
  isSecureContext: boolean;
  validateAction: (action: string) => boolean;
}

const SecurityContext = createContext<SecurityContextType | undefined>(undefined);

interface SecurityProviderProps {
  children: ReactNode;
}

export function SecurityProvider({ children }: SecurityProviderProps) {
  const { isSignedIn } = useProviderAuth();

  useEffect(() => {
    // Initialize security measures when component mounts
    initializeSecurity();
  }, []);

  const isSecureContext = typeof window !== 'undefined' && 
                         (window.location.protocol === 'https:' || 
                          window.location.hostname === 'localhost');

  const validateAction = (action: string): boolean => {
    // Only allow actions if user is properly authenticated
    if (!isSignedIn) {
      console.warn(`[SECURITY] Attempted action '${action}' without authentication`);
      return false;
    }

    const authValidation = validateUserAuthentication();
    if (!authValidation.isValid) {
      console.warn(`[SECURITY] Invalid authentication for action '${action}'`);
      return false;
    }

    return true;
  };

  return (
    <SecurityContext.Provider value={{ isSecureContext, validateAction }}>
      {children}
    </SecurityContext.Provider>
  );
}

export function useSecurity() {
  const context = useContext(SecurityContext);
  if (context === undefined) {
    throw new Error('useSecurity must be used within a SecurityProvider');
  }
  return context;
}
